import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Bell, Loader2 } from "lucide-react";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export const PushActivationPrompt = () => {
  const [open, setOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const [activating, setActivating] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") return;
    if (sessionStorage.getItem("qa-push-prompt-dismissed") === "1") return;
    let mounted = true;
    const timer = setTimeout(async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!mounted || sub) return;
        setDenied(Notification.permission === "denied");
        setOpen(true);
      } catch (e) { /* noop */ }
    }, 3000);
    return () => { mounted = false; clearTimeout(timer); };
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem("qa-push-prompt-dismissed", "1");
    setOpen(false);
  };

  const activar = async () => {
    setActivating(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setDenied(permission === "denied");
        toast.error("Permiso de notificaciones no concedido");
        return;
      }
      const keyRes = await axios.get(`${API}/push-notifications/vapid-key`);
      if (!keyRes.data.configured) {
        toast.error("Notificaciones no configuradas en el servidor");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.publicKey),
      });
      const ua = navigator.userAgent;
      const deviceName = ua.includes("iPhone") ? "iPhone" : ua.includes("iPad") ? "iPad" : ua.includes("Android") ? "Android" : ua.includes("Windows") ? "Windows PC" : ua.includes("Mac") ? "Mac" : "Navegador Web";
      await axios.post(`${API}/push-notifications/subscribe`, {
        subscription: subscription.toJSON(),
        device_name: deviceName,
        device_type: /Mobi|Android/i.test(ua) ? "mobile" : "desktop",
      }, getAuthHeaders());
      toast.success("¡Notificaciones activadas en este dispositivo!");
      sessionStorage.setItem("qa-push-prompt-dismissed", "1");
      setOpen(false);
    } catch (e) {
      console.error("[PushPrompt] error:", e);
      toast.error("No se pudieron activar las notificaciones");
    } finally {
      setActivating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-md" data-testid="push-activation-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#0C314C]" />
            Activa las notificaciones
          </DialogTitle>
          <DialogDescription>
            {denied
              ? "Las notificaciones están bloqueadas en este navegador. Haz clic en el candado junto a la dirección del sitio, permite las Notificaciones y recarga la página."
              : "Para recibir avisos de conversaciones de WhatsApp asignadas y otras alertas de la plataforma, activa las notificaciones de este navegador."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={dismiss} data-testid="push-prompt-later-btn">Ahora no</Button>
          {!denied && (
            <Button onClick={activar} disabled={activating} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="push-prompt-activate-btn">
              {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Activar ahora
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PushActivationPrompt;
